import { useMemo } from 'react'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  type LayoutBox,
  computeLayoutShell,
  computeRotationOrigin,
  computeShellBounds,
  computeViewBoxRect,
} from '../helpers/shape-shell-layout'

export interface ShapeShellLayout {
  /** Shell box in shape-local space (before pixel snapping). */
  shellBounds: LayoutBox
  /** Shell div's CSS box in canvas space (after pixel snapping). */
  layoutShell: LayoutBox
  /** SVG viewBox, tracking `layoutShell` in shape-local units. */
  viewBoxRect: LayoutBox
  /** CSS `transform-origin` pivoting on the geometric centre; undefined for a zero-size shape. */
  rotationOrigin: string | undefined
}

/**
 * Memoized shell geometry (see `helpers/shape-shell-layout.ts` for why each box exists). Memo
 * inputs are primitives so a new `shape` object with the same geometry doesn't recompute.
 */
export function useShapeShellLayout(
  shape: ShapeData,
  pathLayoutBounds: ShapeBounds | null | undefined,
  canvasZoom: number | undefined,
  suppressLayoutPixelSnap: boolean
): ShapeShellLayout {
  const { x, y, width, height, path } = shape

  const shellBounds = useMemo(
    () => computeShellBounds({ width, height, path }, pathLayoutBounds),
    // Primitives, not the bounds object: callers pass a fresh object with the same values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      width,
      height,
      path,
      pathLayoutBounds?.x,
      pathLayoutBounds?.y,
      pathLayoutBounds?.width,
      pathLayoutBounds?.height,
    ]
  )

  const layoutShell = useMemo(
    () => computeLayoutShell(x, y, shellBounds, canvasZoom, suppressLayoutPixelSnap),
    [x, y, shellBounds, canvasZoom, suppressLayoutPixelSnap]
  )

  const viewBoxRect = useMemo(() => computeViewBoxRect(layoutShell, x, y), [layoutShell, x, y])

  const rotationOrigin = useMemo(
    () => computeRotationOrigin({ x, y, width, height }, layoutShell),
    [x, y, width, height, layoutShell]
  )

  return { shellBounds, layoutShell, viewBoxRect, rotationOrigin }
}
